import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  createToolRequest,
  listToolLocations
} from '../../../services/toolRequestService';
import { listProjects } from '../../../services/projectService';

/**
 * Shared "Solicitar herramienta" modal for tools where
 * `requires_approval = true`. Wraps `create_tool_request` (MCP) and
 * lets the requester (técnico / líder) attach an optional project,
 * requested location, and reason that the supervisor will see when
 * approving.
 *
 * Used by:
 *   - WarehousePage (mobile list)
 *   - WarehouseBrowseModal (desktop quick-pick)
 *
 * Props:
 *   show       boolean
 *   onHide     () => void
 *   tool       { id, name, serial_number, image_url, requires_approval }
 *   onSuccess  optional callback after a successful submit (cache busts, toasts)
 */
const ToolRequestModal = ({ show, onHide, tool, onSuccess }) => {
  const queryClient = useQueryClient();
  const [projectId, setProjectId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  // Reset state every time the modal opens for a different tool.
  useEffect(() => {
    if (show) {
      setProjectId('');
      setLocationId('');
      setReason('');
      setError('');
    }
  }, [show, tool?.id]);

  const { data: projectsData } = useQuery({
    queryKey: ['tool-request-projects'],
    queryFn: () => listProjects({ pageSize: 100 }),
    enabled: show,
    staleTime: 60 * 1000
  });
  const projects = projectsData?.data || [];

  const { data: locationsData } = useQuery({
    queryKey: ['tool-request-locations'],
    queryFn: () => listToolLocations({ active_only: true }),
    enabled: show,
    staleTime: 5 * 60 * 1000
  });
  const locations = locationsData?.data || [];

  const mutation = useMutation({
    mutationFn: () => createToolRequest({
      tool_id: tool?.id,
      project_id: projectId || null,
      requested_location_id: locationId || null,
      reason: reason.trim() || null
    }),
    onSuccess: ({ error: err, message }) => {
      if (err) {
        setError(err.message || 'No se pudo crear la solicitud.');
        return;
      }
      toast.success(message || 'Solicitud enviada al supervisor.');
      queryClient.invalidateQueries({ queryKey: ['my-tool-requests'] });
      queryClient.invalidateQueries({ queryKey: ['tool-requests'] });
      onSuccess?.();
      onHide();
    },
    onError: (e) => setError(e.message)
  });

  if (!tool) return null;

  const submit = () => {
    setError('');
    mutation.mutate();
  };

  return (
    <Modal show={show} onHide={onHide} centered fullscreen="md-down">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-clipboard-check me-2" />Solicitar herramienta
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <div className="d-flex gap-3 align-items-center mb-3 p-2 bg-light rounded">
          {tool.image_url ? (
            <img
              src={tool.image_url}
              alt=""
              style={{
                width: 56, height: 56, objectFit: 'cover',
                borderRadius: 8, flexShrink: 0
              }}
            />
          ) : (
            <div style={{
              width: 56, height: 56, background: '#fff',
              borderRadius: 8, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              flexShrink: 0
            }}>
              <i className="bi bi-tools text-muted fs-4" />
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <strong>{tool.name}</strong>
              <Badge bg="warning" text="dark">
                <i className="bi bi-shield-check me-1" />
                Requiere aprobación
              </Badge>
            </div>
            <small className="text-muted font-monospace d-block">
              {tool.serial_number}
            </small>
          </div>
        </div>

        <Alert variant="info" className="d-flex gap-2 small mb-3">
          <i className="bi bi-info-circle flex-shrink-0 mt-1" />
          <div>
            Esta herramienta requiere aprobación de un supervisor antes de
            ser asignada. Tu solicitud entrará a la bandeja del supervisor;
            cuando la apruebe se te asignará automáticamente.
          </div>
        </Alert>

        <Form.Group className="mb-3">
          <Form.Label>
            Proyecto <span className="text-muted small">(opcional)</span>
          </Form.Label>
          <Form.Select
            value={projectId}
            onChange={e => setProjectId(e.target.value)}
            disabled={mutation.isPending}
          >
            <option value="">— Sin proyecto —</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>
                {p.project_number ? `${p.project_number} — ` : ''}{p.title}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            Ubicación deseada <span className="text-muted small">(opcional)</span>
          </Form.Label>
          <Form.Select
            value={locationId}
            onChange={e => setLocationId(e.target.value)}
            disabled={mutation.isPending}
          >
            <option value="">— Sin ubicación específica —</option>
            {locations.map(l => (
              <option key={l.id} value={l.id}>
                [{l.type}] {l.label}{l.code ? ` (${l.code})` : ''}
              </option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            Dónde vas a usar la herramienta. Ayuda al supervisor a decidir.
          </Form.Text>
        </Form.Group>

        <Form.Group>
          <Form.Label>
            Razón <span className="text-muted small">(recomendado)</span>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Para qué la necesitás, cuándo, etc. El supervisor verá este texto."
            disabled={mutation.isPending}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onHide}
          disabled={mutation.isPending}
        >
          Cancelar
        </Button>
        <Button
          variant="primary"
          onClick={submit}
          disabled={mutation.isPending}
        >
          {mutation.isPending && (
            <Spinner size="sm" animation="border" className="me-1" />
          )}
          <i className="bi bi-send me-1" />
          Enviar solicitud
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ToolRequestModal;
